import React, { useState, useEffect } from 'react';
import '../styles/Dashboard.css';

const logosBancos = {
  itau: '🏦',
  c6: '💳',
  nubank: '💜',
  stone: '🪨'
};

const nomeBancos = {
  itau: 'Itaú',
  c6: 'C6',
  nubank: 'NuBank',
  stone: 'Stone'
};

const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());

  useEffect(() => {
    carregarDados();
  }, [mes, ano]);

  const carregarDados = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/dashboard?mes=${mes}&ano=${ano}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const d = await res.json();
      setData(d.summary);
    } catch (err) {
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMesAnterior = () => {
    if (mes === 1) {
      setMes(12);
      setAno(ano - 1);
    } else {
      setMes(mes - 1);
    }
  };

  const handleProximoMes = () => {
    if (mes === 12) {
      setMes(1);
      setAno(ano + 1);
    } else {
      setMes(mes + 1);
    }
  };

  const handleMesAtual = () => {
    const agora = new Date();
    setMes(agora.getMonth() + 1);
    setAno(agora.getFullYear());
  };

  if (loading) return <div className="loading">Carregando dados...</div>;
  if (!data) return <div className="error">Erro ao carregar dashboard</div>;

  const { saldoReal, saldoProjetado, totalReceita, totalDespesa, totalDivida, totalInvestido, contasPorBanco } = data;

  const saldoRealColor = saldoReal >= 0 ? '#10b981' : '#ef4444';
  const saldoProjetadoColor = saldoProjetado >= 0 ? '#10b981' : '#ef4444';

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>📊 Dashboard Financeiro</h1>

        {/* Seletor de Mês */}
        <div className="mes-selector">
          <button onClick={handleMesAnterior} className="btn-nav">◀</button>

          <div className="mes-display">
            <span className="mes-nome">{meses[mes - 1]}</span>
            <span className="ano-nome">{ano}</span>
          </div>

          <button onClick={handleProximoMes} className="btn-nav">▶</button>

          <button onClick={handleMesAtual} className="btn-hoje">Hoje</button>
        </div>
      </div>

      {/* Cards Principais */}
      <div className="summary-grid">
        <div className="card primary">
          <div className="card-header">
            <h3>Saldo Real</h3>
            <span className="icon">💰</span>
          </div>
          <p className="amount" style={{ color: saldoRealColor }}>
            R$ {saldoReal.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
          </p>
          <p className="subtitle">Disponível agora</p>
        </div>

        <div className="card warning">
          <div className="card-header">
            <h3>Saldo Projetado</h3>
            <span className="icon">📈</span>
          </div>
          <p className="amount" style={{ color: saldoProjetadoColor }}>
            R$ {saldoProjetado.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
          </p>
          <p className="subtitle">Após despesas</p>
        </div>

        <div className="card receita">
          <div className="card-header">
            <h3>Receitas</h3>
            <span className="icon">📥</span>
          </div>
          <p className="amount">R$ {totalReceita.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
          <p className="subtitle">{meses[mes - 1]}</p>
        </div>

        <div className="card despesa">
          <div className="card-header">
            <h3>Despesas</h3>
            <span className="icon">📤</span>
          </div>
          <p className="amount">R$ {totalDespesa.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
          <p className="subtitle">{meses[mes - 1]}</p>
        </div>

        <div className="card investimento">
          <div className="card-header">
            <h3>Investimentos</h3>
            <span className="icon">📈</span>
          </div>
          <p className="amount">R$ {totalInvestido.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
          <p className="subtitle">Total investido</p>
        </div>

        <div className="card divida">
          <div className="card-header">
            <h3>Dívidas Ativas</h3>
            <span className="icon">💳</span>
          </div>
          <p className="amount">R$ {totalDivida.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
          <p className="subtitle">Total</p>
        </div>
      </div>

      {/* Contas por Banco */}
      <div className="contas-por-banco">
        <h2>🏦 Contas por Banco</h2>
        <div className="bancos-grid">
          {Object.entries(contasPorBanco).map(([banco, contas]) => (
            <div key={banco} className="banco-card">
              <div className="banco-header">
                <span className="logo">{logosBancos[banco]}</span>
                <span className="nome">{nomeBancos[banco]}</span>
                <span className="badge">{contas.length}</span>
              </div>
              <div className="contas-list">
                {contas.map((conta, idx) => (
                  <div key={idx} className="conta-item">
                    <span className="conta-nome">{conta.nome}</span>
                    <span className="conta-saldo">
                      R$ {conta.saldo.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                    </span>
                  </div>
                ))}
              </div>
              <div className="banco-total">
                <span>Total {nomeBancos[banco]}</span>
                <strong>
                  R$ {contas.reduce((sum, c) => sum + c.saldo, 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                </strong>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Indicadores do Mês */}
      <div className="indicators">
        <h2>📊 Indicadores de {meses[mes - 1]}</h2>
        <div className="indicators-grid">
          <div className="indicator">
            <span className="label">Taxa de Economia</span>
            <p className="value">
              {totalReceita > 0
                ? (((totalReceita - totalDespesa) / totalReceita * 100).toFixed(1))
                : 0}%
            </p>
          </div>
          <div className="indicator">
            <span className="label">Despesa/Receita</span>
            <p className="value">
              {totalReceita > 0
                ? ((totalDespesa / totalReceita * 100).toFixed(1))
                : 0}%
            </p>
          </div>
          <div className="indicator">
            <span className="label">Saldo do Mês</span>
            <p className="value" style={{ color: (totalReceita - totalDespesa) >= 0 ? '#10b981' : '#ef4444' }}>
              R$ {(totalReceita - totalDespesa).toLocaleString('pt-BR', {minimumFractionDigits: 2})}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
